/**
 * Тексты юридических документов (A-09, SYS-05, A-05).
 *
 * ПС §06/§15: правила сообщества, условия использования и политика
 * приватности открываются внутри клиента целиком, а не ссылкой наружу.
 * Действующая редакция (дата) приходит из `GET /config → legal[doc].version`;
 * содержимое — здесь, отдельными разделами, чтобы экран мог собрать
 * оглавление и якоря.
 *
 * Реквизиты оператора собраны в `OPERATOR`: при смене юридического лица
 * меняется одно место, а не десяток абзацев.
 */
import type { LegalDocument } from '@shared/api/types'

export interface LegalSection {
  id: string
  title: string
  /** Абзацы; строка, начинающаяся с «— », становится пунктом списка. */
  paragraphs: readonly string[]
}

export interface LegalText {
  id: LegalDocument
  title: string
  /** Короткое объяснение, о чём документ, — под заголовком. */
  summary: string
  sections: readonly LegalSection[]
}

/** Оператор сервиса — заполняется реквизитами юридического лица. */
export const OPERATOR = {
  name: 'ООО «Движ»',
  service: 'сервис «Движ» (dvizh.chat)',
  address: 'Российская Федерация, адрес указывается в реквизитах оператора',
  email: 'support@dvizh.chat',
  privacyEmail: 'privacy@dvizh.chat',
} as const

export const TERMS: LegalText = {
  id: 'terms',
  title: 'Условия использования',
  summary:
    'Правила, на которых вы пользуетесь «Движем»: кто может зарегистрироваться, что можно и нельзя делать, как устроены аккаунт, контент и ответственность сторон.',
  sections: [
    {
      id: 'general',
      title: '1. Общие положения',
      paragraphs: [
        `Настоящие Условия использования (далее — Условия) регулируют отношения между ${OPERATOR.name} (далее — Оператор) и пользователем (далее — Пользователь) при использовании ${OPERATOR.service} — сервиса для знакомств, поиска друзей и общения (далее — Сервис).`,
        'Условия являются публичной офертой в соответствии со статьёй 437 Гражданского кодекса Российской Федерации. Регистрируясь в Сервисе или продолжая им пользоваться, Пользователь принимает Условия полностью и без оговорок. Если Пользователь не согласен с каким-либо положением, он обязан прекратить использование Сервиса.',
        'Неотъемлемой частью Условий являются Политика приватности и Правила сообщества. При противоречии специальные правила (Правила сообщества) имеют приоритет в вопросах допустимого поведения, Политика приватности — в вопросах обработки персональных данных.',
      ],
    },
    {
      id: 'age',
      title: '2. Кто может пользоваться Сервисом',
      paragraphs: [
        'Сервис предназначен только для лиц, достигших 18 лет. Регистрация лиц младше 18 лет запрещена. Оператор вправе запросить подтверждение возраста и приостановить аккаунт до его получения.',
        'Пользователь подтверждает, что действует от своего имени, обладает необходимой дееспособностью и что предоставленные им сведения достоверны.',
        'Аккаунт принадлежит одному человеку. Передавать доступ к аккаунту третьим лицам, продавать аккаунт или регистрировать несколько аккаунтов для обхода ограничений запрещено.',
      ],
    },
    {
      id: 'account',
      title: '3. Аккаунт и вход',
      paragraphs: [
        'Вход в Сервис выполняется по одноразовому коду, отправляемому на подтверждённый телефон или адрес электронной почты. Пароль не создаётся. Пользователь обязан обеспечивать сохранность доступа к своим контактам и незамедлительно сообщать Оператору о подозрении на несанкционированный доступ.',
        'Пользователь может добавить несколько способов входа, завершить активные сеансы на других устройствах и удалить аккаунт в настройках. При удалении аккаунта действует срок восстановления, после которого данные удаляются в порядке, описанном в Политике приватности.',
        'Дата рождения указывается при регистрации один раз и впоследствии изменяется только через службу поддержки с проверкой: на ней основана проверка возраста.',
      ],
    },
    {
      id: 'content',
      title: '4. Профиль и содержимое',
      paragraphs: [
        'Пользователь размещает в профиле фотографии, имя, описание и другие сведения (далее — Контент). Пользователь гарантирует, что обладает правами на Контент и что его размещение не нарушает прав третьих лиц и законодательства Российской Федерации.',
        'Фотографии и публичные текстовые поля профиля проходят проверку. До её завершения новые материалы видны только Пользователю. Оператор вправе отклонить Контент, не соответствующий Правилам сообщества, с указанием категории причины.',
        'Размещая Контент, Пользователь предоставляет Оператору неисключительное, безвозмездное право использовать его в объёме, необходимом для работы Сервиса: показывать другим пользователям в рамках подбора и переписки, создавать уменьшенные копии, хранить и передавать в рамках технической инфраструктуры. Право прекращается при удалении Контента или аккаунта, за исключением копий, сохранение которых требуется законом.',
        'Запрещено размещать в профиле контактные данные, рекламу, ссылки на сторонние ресурсы, а также материалы, перечисленные в Правилах сообщества как недопустимые.',
      ],
    },
    {
      id: 'rules',
      title: '5. Правила поведения',
      paragraphs: [
        'Пользователь обязуется соблюдать Правила сообщества: уважительно относиться к собеседникам, не выдавать себя за другого человека, не рассылать спам, не заниматься мошенничеством и не совершать действий, направленных на причинение вреда другим пользователям или Сервису.',
        'Личная переписка доступна после взаимного интереса (совпадения), с добавленными контактами, а также по никнейму. Никнейм позволяет тем, кто его знает или нашёл в поиске, начать диалог без совпадения. Найти человека по номеру телефона можно с учётом его настроек приватности. Пользователь может в любой момент заблокировать другого пользователя, пожаловаться на профиль, сообщение или вложение, а также завершить совпадение.',
        'Запрещено использовать автоматизированные средства для доступа к Сервису, собирать данные других пользователей, обходить технические ограничения, а также пытаться получить доступ к чужим аккаунтам.',
      ],
    },
    {
      id: 'moderation',
      title: '6. Модерация и ограничения',
      paragraphs: [
        'За нарушение Условий или Правил сообщества Оператор вправе применить меры: предупреждение, ограничение отправки сообщений, скрытие анкеты из рекомендаций, блокировку аккаунта. Вид, срок и возможность обжалования меры сообщаются Пользователю в приложении.',
        'Пользователь вправе обжаловать ограничение через раздел «Ограничения аккаунта». Апелляция рассматривается человеком; решение сообщается уведомлением. Подробности жалоб третьих лиц и внутренние сигналы модерации не раскрываются — это защищает обе стороны.',
        'Оператор вправе удалить Контент и приостановить аккаунт без предварительного уведомления, если есть основания полагать, что действия Пользователя угрожают безопасности других пользователей или нарушают закон.',
      ],
    },
    {
      id: 'service',
      title: '7. Работа Сервиса',
      paragraphs: [
        'Сервис предоставляется «как есть». Оператор стремится обеспечить непрерывную работу, но не гарантирует отсутствие перерывов, ошибок и потерь данных, вызванных обстоятельствами вне его контроля, в том числе действиями операторов связи и провайдеров.',
        'Оператор вправе изменять функциональность Сервиса, вводить и отменять функции, устанавливать технические ограничения (размеры файлов, число фотографий, частоту действий). Актуальные ограничения отображаются в приложении.',
        'Рекомендации формируются автоматически на основе указанных Пользователем параметров подбора и активности в Сервисе. Оператор не гарантирует появление совпадений и не несёт ответственности за поведение других пользователей вне Сервиса.',
      ],
    },
    {
      id: 'liability',
      title: '8. Ответственность',
      paragraphs: [
        'Пользователь самостоятельно несёт ответственность за размещаемый Контент, за свои действия в Сервисе и за общение с другими пользователями, включая встречи вне Сервиса. Оператор рекомендует соблюдать меры безопасности: встречаться в общественных местах, не передавать деньги и документы, сообщать близким о встрече.',
        'Оператор не является стороной отношений между пользователями и не несёт ответственности за ущерб, возникший в результате таких отношений, за исключением случаев, прямо предусмотренных законом.',
        'Ответственность Оператора перед Пользователем в любом случае ограничена суммой, уплаченной Пользователем за использование платных функций Сервиса за последние 12 месяцев, а при отсутствии платежей — 1 000 рублей.',
      ],
    },
    {
      id: 'changes',
      title: '9. Изменение Условий',
      paragraphs: [
        'Оператор вправе изменять Условия. Новая редакция вступает в силу с даты, указанной в ней. При существенных изменениях Сервис запрашивает повторное принятие документов перед продолжением использования.',
        'Действующая редакция и дата её вступления в силу всегда доступны в приложении в разделе «Документы».',
      ],
    },
    {
      id: 'contacts',
      title: '10. Применимое право и контакты',
      paragraphs: [
        'К отношениям сторон применяется законодательство Российской Федерации. Споры разрешаются путём переговоров, а при недостижении согласия — в суде по месту нахождения Оператора, если иное не установлено законом о защите прав потребителей.',
        `Обращения по вопросам работы Сервиса направляются через раздел «Помощь и поддержка» в приложении или по адресу ${OPERATOR.email}. Оператор: ${OPERATOR.name}, ${OPERATOR.address}.`,
      ],
    },
  ],
}

export const PRIVACY: LegalText = {
  id: 'privacy',
  title: 'Политика приватности',
  summary:
    'Какие данные мы получаем, зачем они нужны, кто их видит, сколько мы их храним и как вы можете ими управлять.',
  sections: [
    {
      id: 'general',
      title: '1. О чём этот документ',
      paragraphs: [
        `Политика приватности (далее — Политика) описывает, как ${OPERATOR.name} (далее — Оператор) обрабатывает персональные данные пользователей ${OPERATOR.service} в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных».`,
        'Принимая Условия использования, Пользователь даёт согласие на обработку персональных данных в объёме и целях, описанных в Политике. Согласие может быть отозвано удалением аккаунта или обращением к Оператору.',
      ],
    },
    {
      id: 'data',
      title: '2. Какие данные мы обрабатываем',
      paragraphs: [
        '— Данные для входа: номер телефона и/или адрес электронной почты, коды подтверждения, сведения об устройствах и сеансах (тип устройства, браузер, приблизительный город по IP-адресу без сохранения самого адреса).',
        '— Данные профиля: имя или ник, дата рождения (для проверки возраста; другим показывается только возраст), пол, город, фотографии, рассказ о себе, род занятий, интересы, языки, параметры подбора.',
        '— Местоположение: только выбранный город. Точные координаты не запрашиваются и не сохраняются; другим пользователям виден город, а подбор идёт по городам, которые вы выбрали сами.',
        '— Активность в Сервисе: лайки и пропуски, совпадения, переписка (текст, фотографии, видео, голосовые сообщения, реакции), жалобы и обращения в поддержку.',
        '— Технические данные: идентификатор установки, версия приложения, журналы ошибок без содержимого сообщений, токены push-уведомлений.',
        'Оператор не запрашивает и не обрабатывает специальные категории данных (о здоровье, политических взглядах и т. п.), если Пользователь сам не разместил такие сведения в профиле или переписке.',
      ],
    },
    {
      id: 'purposes',
      title: '3. Зачем мы обрабатываем данные',
      paragraphs: [
        '— Предоставление Сервиса: регистрация и вход, показ анкеты подходящим людям, переписка, уведомления.',
        '— Безопасность: проверка возраста, защита от мошенничества, спама и подделки аккаунтов, обработка жалоб и апелляций, ограничение нарушителей.',
        '— Модерация содержимого: проверка фотографий и текстов профиля, включая автоматические проверки и проверку человеком.',
        '— Улучшение Сервиса: анализ обезличенной статистики использования.',
        '— Исполнение требований закона: ответы на законные запросы государственных органов.',
        'Оператор не продаёт персональные данные и не использует переписку пользователей для рекламы.',
      ],
    },
    {
      id: 'visibility',
      title: '4. Кто видит ваши данные',
      paragraphs: [
        'Другим пользователям доступна только публичная часть анкеты: проверенные фотографии, имя, возраст, город, рассказ о себе, интересы, род занятий, режим подбора. Телефон, адрес почты, дата рождения и статусы модерации другим не показываются никогда.',
        'Присутствие в сети и время последней активности показываются приблизительно и только пользователям с активным совпадением; эти настройки взаимны и отключаются в разделе «Приватность».',
        'Содержимое переписки доступно только её участникам. Сотрудники Оператора получают доступ к отдельным сообщениям только в рамках рассмотрения жалобы, к которой прилагается контекст переписки, или по требованию закона.',
        'Push-уведомления не содержат текста сообщений и имён: превью загружается на устройство только при открытии уведомления и только если это разрешено в настройках.',
      ],
    },
    {
      id: 'processors',
      title: '5. Кому мы передаём данные',
      paragraphs: [
        'Для работы Сервиса Оператор привлекает технических подрядчиков: хостинг и хранение данных на территории Российской Федерации, доставку SMS и электронной почты, доставку push-уведомлений (Web Push, Firebase Cloud Messaging для Android). Подрядчики получают только те данные, которые необходимы для их задачи, и обязаны соблюдать конфиденциальность.',
        'Данные могут быть переданы государственным органам в случаях и порядке, установленных законодательством Российской Федерации.',
      ],
    },
    {
      id: 'retention',
      title: '6. Сколько мы храним данные',
      paragraphs: [
        '— Данные аккаунта и профиля — пока существует аккаунт. После запроса на удаление действует срок восстановления (30 дней), затем данные удаляются или обезличиваются.',
        '— Переписка — пока существуют аккаунты обоих участников; «удалить у себя» скрывает сообщения только для вас, «удалить у всех» — для обоих.',
        '— Коды подтверждения — до 10 минут; журналы безопасности (входы, завершения сеансов) — до 12 месяцев.',
        '— Жалобы, апелляции и решения модерации — до 3 лет с момента решения, чтобы предотвращать повторные нарушения.',
        'Сведения, хранение которых требуется законом, сохраняются в течение установленного им срока.',
      ],
    },
    {
      id: 'rights',
      title: '7. Ваши права и настройки',
      paragraphs: [
        '— Посмотреть и изменить данные профиля — в разделе «Профиль».',
        '— Управлять видимостью: поставить анкету на паузу, скрыть присутствие в сети и подтверждения прочтения — в «Настройках».',
        '— Получить копию своих данных — «Настройки → Мои данные»: архив готовится и присылается уведомлением, ссылка действует 24 часа.',
        '— Удалить аккаунт — «Настройки → Удаление аккаунта».',
        '— Отозвать согласие, уточнить или потребовать прекратить обработку данных — обращением к Оператору.',
        `Обращения по персональным данным принимаются по адресу ${OPERATOR.privacyEmail} и рассматриваются в срок, установленный законом.`,
      ],
    },
    {
      id: 'storage',
      title: '8. Данные на вашем устройстве',
      paragraphs: [
        'Приложение хранит на устройстве локальную копию ваших диалогов, черновики и настройки интерфейса, чтобы работать быстрее и офлайн. Эти данные удаляются при выходе из аккаунта.',
        'Cookie используются только для поддержания сеанса входа. Рекламные и сторонние трекеры не используются.',
      ],
    },
    {
      id: 'security',
      title: '9. Как мы защищаем данные',
      paragraphs: [
        'Данные передаются по защищённому соединению (HTTPS). Доступ сотрудников к данным ограничен ролями и журналируется. Токены и контактные данные хранятся в зашифрованном виде. При выявлении инцидента, затрагивающего ваши данные, Оператор уведомляет пользователей и уполномоченный орган в установленном порядке.',
      ],
    },
    {
      id: 'changes',
      title: '10. Изменения Политики',
      paragraphs: [
        'Оператор вправе обновлять Политику. Дата действующей редакции указана в приложении в разделе «Документы»; при существенных изменениях Сервис запросит повторное принятие.',
        `Оператор: ${OPERATOR.name}, ${OPERATOR.address}. Контакт по вопросам персональных данных: ${OPERATOR.privacyEmail}.`,
      ],
    },
  ],
}

export const COMMUNITY_RULES: LegalText = {
  id: 'community_rules',
  title: 'Правила сообщества',
  summary:
    'Что принято и что запрещено в «Движе»: уважение к собеседникам, честный профиль, безопасность. Нарушение правил ведёт к ограничениям вплоть до блокировки.',
  sections: [
    {
      id: 'respect',
      title: '1. Уважайте собеседника',
      paragraphs: [
        'Общайтесь так, как хотели бы, чтобы общались с вами. Запрещены оскорбления, унижение, угрозы, травля и навязчивые сообщения после того, как человек попросил прекратить общение или перестал отвечать.',
        'Отказ — это ответ. Не требуйте объяснений, не давите и не пытайтесь связаться другими способами.',
        'Запрещены дискриминация и разжигание ненависти по признаку пола, возраста, национальности, религии, ориентации, внешности и любым другим.',
      ],
    },
    {
      id: 'honesty',
      title: '2. Будьте собой',
      paragraphs: [
        'В профиле — ваши настоящие фотографии и правдивые сведения. Запрещено выдавать себя за другого человека, использовать чужие фото, сильно изменённые изображения или изображения, по которым нельзя узнать вас.',
        'Один человек — один аккаунт. Создание нескольких аккаунтов для обхода блокировки или манипуляции рекомендациями запрещено.',
        'Возраст в профиле должен быть настоящим. Сервис только для совершеннолетних.',
      ],
    },
    {
      id: 'safety',
      title: '3. Безопасность прежде всего',
      paragraphs: [
        'Запрещены мошенничество, просьбы о деньгах, «инвестиционные предложения», выманивание данных карт и документов, ссылки на подозрительные сайты.',
        'Запрещены спам, массовые рассылки, реклама услуг и товаров, продвижение других сервисов, а также размещение контактов и ссылок в профиле.',
        'Не делитесь данными документов, адресом и финансовой информацией. Встречайтесь в общественных местах и предупреждайте близких.',
      ],
    },
    {
      id: 'content',
      title: '4. Какое содержимое недопустимо',
      paragraphs: [
        '— Обнажённое тело, сексуальный контент и намёки на сексуальные услуги.',
        '— Насилие, оружие, жестокость, самоповреждение, наркотики.',
        '— Контент с участием несовершеннолетних в любом контексте, кроме семейных фотографий, где присутствует взрослый пользователь; на главном фото должны быть только вы.',
        '— Контактные данные, QR-коды, водяные знаки и рекламные надписи на фотографиях.',
        '— Материалы, нарушающие законодательство Российской Федерации или права третьих лиц.',
        'Фотографии проходят проверку до показа другим. Отклонённое фото сопровождается категорией причины; его можно заменить.',
      ],
    },
    {
      id: 'reports',
      title: '5. Жалобы и блокировки',
      paragraphs: [
        'Если что-то не так — пожалуйтесь на профиль, сообщение или вложение прямо из диалога или профиля. Жалобы рассматриваются в первую очередь; к жалобе на сообщение прилагаются соседние сообщения, чтобы решение принималось по разговору.',
        'Заблокируйте человека, если он вызывает беспокойство: вы перестанете видеть друг друга, совпадение закроется. Мы не сообщаем, кто пожаловался или заблокировал.',
        'Заведомо ложные жалобы — тоже нарушение.',
      ],
    },
    {
      id: 'sanctions',
      title: '6. Что бывает за нарушения',
      paragraphs: [
        'В зависимости от тяжести и повторности: предупреждение, ограничение отправки сообщений, скрытие анкеты из рекомендаций, блокировка аккаунта. Срок и возможность обжалования сообщаются в уведомлении и в разделе «Ограничения аккаунта».',
        'Решение можно обжаловать один раз по каждому ограничению; апелляцию читает человек. Мошенничество, угрозы безопасности и контент с несовершеннолетними ведут к немедленной блокировке без права восстановления.',
      ],
    },
  ],
}

export const LEGAL_TEXTS: Record<LegalDocument, LegalText> = {
  terms: TERMS,
  privacy: PRIVACY,
  community_rules: COMMUNITY_RULES,
}

export function isLegalDocument(value: string): value is LegalDocument {
  return value === 'terms' || value === 'privacy' || value === 'community_rules'
}
